<template>
  <!-- Loading: shimmer skeleton block -->
  <figure
    v-if="isLoading"
    class="media-content media-content--loading rounded-size-5"
    aria-hidden="true"
  >
    <div class="media-content__shimmer"></div>
  </figure>

  <figure
    v-else
    class="media-content rounded-size-5"
    :class="[`media-content--${resolvedType}`]"
    role="group"
    :aria-label="groupLabel"
  >
    <!-- ── Variant: image ─────────────────────────────────────────── -->
    <template v-if="resolvedType === 'image'">
      <img
        class="media-content__media"
        :src="imageSrc"
        :alt="alt"
        loading="lazy"
        @error="onMediaError"
      />
    </template>

    <!-- ── Variant: video (.mp4 / other) ──────────────────────────── -->
    <template v-else-if="resolvedType === 'video'">
      <video
        ref="videoRef"
        class="media-content__media"
        :src="videoSrc"
        muted
        playsinline
        loop
        :aria-label="alt || 'Vídeo'"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @ended="isPlaying = false"
        @error="videoError = true"
      ></video>

      <IconButton
        class="media-content__control"
        type="inverted"
        variant="primary"
        size="xl"
        shadow
        @clicked="togglePlay"
      >
        <template #trailingIcon>
          <PauseIcon
            v-if="isPlaying"
            class="media-content__control-icon"
            role="img"
            aria-label="Pausar vídeo"
          />
          <PlayIcon
            v-else
            class="media-content__control-icon"
            role="img"
            aria-label="Reproduzir vídeo"
          />
        </template>
      </IconButton>

      <!-- Status message (WCAG 4.1.3): anuncia o estado do vídeo. -->
      <p class="media-content__status sr-only" role="status" aria-live="polite">
        {{ videoStatusMessage }}
      </p>
    </template>

    <!-- ── Variant: thumbnail (thumbnail + overlay message) ─────── -->
    <template v-else-if="resolvedType === 'thumbnail'">
      <img
        class="media-content__media"
        :src="thumbnailSrc"
        :alt="alt"
        loading="lazy"
        @error="onMediaError"
      />

      <span
        v-if="duration"
        class="media-content__duration caption-medium"
        :aria-label="`Duração ${duration}`"
      >
        <RelogioIcon class="media-content__duration-icon" aria-hidden="true" />
        {{ duration }}
      </span>

      <div class="media-content__overlay">
        <div class="media-content__spacer" aria-hidden="true"></div>

        <CaModuleMessage
          class="media-content__message"
          theme="inverted"
          size="S"
          :label="label"
          :title="title"
          :title-heading-type="titleHeadingType"
          :description="description"
          :description-heading-type="descriptionHeadingType"
        />

        <BaseButton
          v-if="youtubeUrl"
          class="media-content__cta inverted primary"
          :label="buttonLabel"
          aria-label="Ver vídeo. Link. Abre numa nova janela"
          @click="openYoutube"
        >
          <template #trailingIcon>
            <PlayIcon class="media-content__cta-icon" aria-hidden="true" />
          </template>
        </BaseButton>
      </div>
    </template>
  </figure>
</template>

<script setup>
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { BaseButton, IconButton } from "ca-online-design-system";
import CaModuleMessage from "../moduleMessage/CaModuleMessage.vue";
import PlayIcon from "@assets/play.svg";
import PauseIcon from "@assets/pause.svg";
import RelogioIcon from "@assets/relogio.svg";
// Placeholders (SVG) importados como raw e convertidos em data URI para
// ficarem embutidos no bundle UMD, onde os ficheiros de asset separados não
// são enviados.
import placeholderRaw from "@assets/media_content_placeholder.svg?raw";
const placeholderImage = `data:image/svg+xml,${encodeURIComponent(
  placeholderRaw,
)}`;
// Placeholder para mobile (SVG).
import mobilePlaceholderRaw from "@assets/media_content_placeholder_mobile.svg?raw";
const mobilePlaceholderImage = `data:image/svg+xml,${encodeURIComponent(
  mobilePlaceholderRaw,
)}`;

defineOptions({
  name: "CaMediaContent",
});

const props = defineProps({
  /**
   * Tipo de media a apresentar. Quando omitido é inferido a partir das props:
   * youtubeUrl -> "thumbnail", videoSrc -> "video", caso contrário "image".
   */
  type: {
    type: String,
    default: "",
    validator: (v) => ["", "image", "video", "thumbnail"].includes(v),
  },
  /** Image URL (image variant, and fallback thumbnail for the others). */
  image: {
    type: String,
    default: "",
  },
  /** Image URL usada na versão mobile (image variant). */
  imageMobile: {
    type: String,
    default: "",
  },
  /** Video source URL (.mp4 or any browser-supported extension). */
  videoSrc: {
    type: String,
    default: "",
  },
  /** YouTube video URL opened by the CTA button. */
  youtubeUrl: {
    type: String,
    default: "",
  },
  /** Thumbnail image used by the youtube (and video poster) variant. */
  thumbnail: {
    type: String,
    default: "",
  },
  /** Thumbnail usada na versão mobile (thumbnail variant). */
  thumbnailMobile: {
    type: String,
    default: "",
  },
  /** Video duration shown as a badge, e.g. "0:20". */
  duration: {
    type: String,
    default: "",
  },
  /** Alternative text for the image/thumbnail. */
  alt: {
    type: String,
    default: "",
  },
  /** Overlay label (youtube variant). */
  label: {
    type: String,
    default: "",
  },
  /** Overlay title (youtube variant). */
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "Paragraph",
  },
  /** Overlay description (youtube variant). */
  description: {
    type: String,
    default: "",
  },
  descriptionHeadingType: {
    type: String,
    default: "Paragraph",
  },
  /** CTA button label (youtube variant). */
  buttonLabel: {
    type: String,
    default: "Label",
  },
});

const emit = defineEmits(["play", "pause", "open"]);

const videoRef = ref(null);
const isPlaying = ref(false);
const videoError = ref(false);
// True quando o utilizador pausa manualmente; impede o autoplay de retomar.
const userPaused = ref(false);
// True em viewports mobile (< 768px); escolhe o placeholder correspondente.
const isMobile = ref(false);

/** Placeholder ativo consoante o viewport (mobile vs. desktop). */
const activePlaceholder = computed(() =>
  isMobile.value ? mobilePlaceholderImage : placeholderImage,
);

/** Whether a media source is a non-empty, usable string. */
function isValidSrc(src) {
  return typeof src === "string" && src.trim().length > 0;
}

/**
 * Estado de loading derivado internamente: enquanto nenhuma fonte de media
 * (imagem, vídeo ou YouTube) chegou, mostra o shimmer. Assim que os dados
 * estiverem disponíveis, renderiza o conteúdo normal sem placeholders.
 */
const isLoading = computed(
  () =>
    !isValidSrc(props.image) &&
    !isValidSrc(props.videoSrc) &&
    !isValidSrc(props.youtubeUrl) &&
    !isValidSrc(props.thumbnail),
);

/** Nome acessível do grupo/secção (WCAG 1.3.1 / 4.1.2). */
const groupLabel = computed(
  () => props.alt || props.title || props.label || "Conteúdo multimédia",
);

/** Mensagem de estado do vídeo anunciada por leitores de ecrã (WCAG 4.1.3). */
const videoStatusMessage = computed(() =>
  isPlaying.value ? "Vídeo. A reproduzir." : "Vídeo. Em pausa.",
);

/**
 * Image source with placeholder fallback when empty/invalid. Em mobile usa a
 * imagem mobile quando disponível, caindo para a imagem base.
 */
const imageSrc = computed(() => {
  if (isMobile.value && isValidSrc(props.imageMobile)) return props.imageMobile;
  if (isValidSrc(props.image)) return props.image;
  return activePlaceholder.value;
});

/**
 * Thumbnail source (youtube variant) with placeholder fallback. Em mobile usa a
 * thumbnail mobile quando disponível, caindo para a thumbnail base e depois
 * para a imagem.
 */
const thumbnailSrc = computed(() => {
  if (isMobile.value && isValidSrc(props.thumbnailMobile))
    return props.thumbnailMobile;
  if (isValidSrc(props.thumbnail)) return props.thumbnail;
  if (isMobile.value && isValidSrc(props.imageMobile)) return props.imageMobile;
  if (isValidSrc(props.image)) return props.image;
  return activePlaceholder.value;
});

/** Swap a broken image for the placeholder. */
function onMediaError(event) {
  const img = event.target;
  if (img && img.src !== activePlaceholder.value) {
    img.src = activePlaceholder.value;
  }
}

/** Infer the variant from the provided props when not set explicitly. */
const resolvedType = computed(() => {
  const variant =
    props.type ||
    (isValidSrc(props.youtubeUrl)
      ? "thumbnail"
      : isValidSrc(props.videoSrc)
        ? "video"
        : "image");

  // Sem vídeo válido (vazio, inválido ou com erro de carregamento),
  // mostra a imagem (placeholder).
  if (
    variant === "video" &&
    (!isValidSrc(props.videoSrc) || videoError.value)
  ) {
    return "image";
  }

  // Thumbnail inferido (sem type explícito) precisa de um youtubeUrl válido;
  // com type="thumbnail" explícito, permite a variante sem youtubeUrl.
  if (
    variant === "thumbnail" &&
    props.type !== "thumbnail" &&
    !isValidSrc(props.youtubeUrl)
  ) {
    return "image";
  }

  return variant;
});

/** Toggle play/pause on the native video element (video variant). */
function togglePlay() {
  const video = videoRef.value;
  if (!video) return;

  if (video.paused) {
    userPaused.value = false;
    video.play().catch(() => {});
    emit("play");
  } else {
    userPaused.value = true;
    video.pause();
    emit("pause");
  }
}

/** Open the YouTube link in a new tab (youtube variant). */
function openYoutube() {
  emit("open", props.youtubeUrl);
  if (props.youtubeUrl) {
    window.open(props.youtubeUrl, "_blank", "noopener,noreferrer");
  }
}

// ---------------------------------------------------------------------------
// Autoplay the video (muted) once it enters the viewport. It then keeps looping
// forever and is NEVER paused automatically (not even when scrolling out); only
// a user click on the control pauses it. Requires the video to be muted for
// browsers to allow autoplay.
// ---------------------------------------------------------------------------
let observer = null;

function setupViewportAutoplay() {
  const video = videoRef.value;
  if (!video || typeof IntersectionObserver === "undefined") return;

  // Evita observers duplicados se a variante voltar a "video".
  teardownViewportAutoplay();

  // Force the muted property in JS: browsers only allow autoplay for muted
  // videos, and Vue does not reliably reflect the `muted` attribute onto the
  // DOM property.
  video.muted = true;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        // Só arranca quando entra no viewport. Mantém-se o observer ativo para
        // voltar a tentar caso o primeiro play() seja rejeitado (vídeo ainda
        // não pronto). Nunca pausa ao sair do viewport nem retoma depois de o
        // utilizador ter pausado manualmente.
        if (entry.isIntersecting && video.paused && !userPaused.value) {
          video.play().catch(() => {});
        }
      });
    },
    { threshold: 0.25 },
  );

  observer.observe(video);
}

function teardownViewportAutoplay() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}

// Reinicia o estado do vídeo quando a fonte muda: permite recuperar de um erro
// anterior e limpa a pausa manual do vídeo anterior.
watch(
  () => props.videoSrc,
  () => {
    videoError.value = false;
    userPaused.value = false;
  },
);

// Configura (ou remove) o autoplay sempre que a variante muda. Ao contrário de
// um `onMounted` único, isto cobre o caso em que o `<video>` só aparece depois
// do mount (props/dados assíncronos ou fallback de variante).
watch(
  resolvedType,
  (type) => {
    if (type === "video") {
      nextTick(() => setupViewportAutoplay());
    } else {
      teardownViewportAutoplay();
    }
  },
  { immediate: true },
);

// ---------------------------------------------------------------------------
// Deteta viewports mobile para escolher o placeholder adequado.
// ---------------------------------------------------------------------------
let mobileQuery = null;
function handleMobileChange(event) {
  isMobile.value = event.matches;
}

onMounted(() => {
  if (typeof window !== "undefined" && window.matchMedia) {
    mobileQuery = window.matchMedia("(max-width: 767px)");
    isMobile.value = mobileQuery.matches;
    mobileQuery.addEventListener("change", handleMobileChange);
  }
});

onBeforeUnmount(() => {
  if (mobileQuery)
    mobileQuery.removeEventListener("change", handleMobileChange);
  teardownViewportAutoplay();
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Base (mobile first). The figure is a positioning context for overlays and
// controls. Media fills the box with a default portrait aspect ratio that the
// host can override via the --ca-media-aspect custom property.
// ---------------------------------------------------------------------------
.media-content {
  position: relative;
  display: flex;
  margin: 0;
  overflow: hidden;
  background: var(--ca-color-neutrals-on-background-low-10);
  height: 500px;
}

// Todas as variantes seguem a altura natural da media (largura total,
// altura proporcional), em vez de um rácio fixo.
.media-content__media {
  width: 100%;
  height: auto;
  display: block;
  // Mínimo (mobile 500px): se a imagem for mais baixa, faz fill (cover, sem
  // distorcer); se for mais alta, cresce e ocupa mais.
  height: 500px;
  object-fit: cover;
}

// Durante o loading não há media com dimensões intrínsecas, por isso mantém-se
// um rácio para o shimmer não colapsar.
.media-content--loading {
  aspect-ratio: var(--ca-media-aspect, 3 / 4);
  height: 500px;
}

// Loading shimmer fills the whole media box. Self-contained: gradient and
// animation are defined locally. The --ca-shimmer-* custom properties allow an
// ancestor (e.g. a tinted section) to override the colours; the fallbacks keep
// it working standalone.
.media-content__shimmer {
  width: 100%;
  height: 100%;
  border-radius: 0;
  background-image: linear-gradient(
    var(--ca-shimmer-angle, 90deg),
    var(--ca-shimmer-from, var(--ca-color-empresas-solarized-medium, #edecea))
      0%,
    var(--ca-shimmer-to, var(--ca-color-backgrounds-container)) 100%
  );
  background-size: 200% 100%;
  animation: ca-media-content-shimmer 1.5s ease-in-out infinite;
}

@keyframes ca-media-content-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Conteúdo apenas para leitores de ecrã (visualmente escondido).
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// ---------------------------------------------------------------------------
// Video variant — play/pause control (DS IconButton, top-right, mobile first)
// ---------------------------------------------------------------------------
.media-content__control {
  position: absolute;
  top: var(--size-4);
  right: var(--size-4);
  z-index: 2;
}

.media-content__control-icon {
  color: var(--ca-color-theme-primary);
}

// ---------------------------------------------------------------------------
// YouTube variant — dark gradient overlay with message + CTA + duration badge
// A imagem fica no fluxo (define a altura) e o overlay assenta por cima.
// ---------------------------------------------------------------------------
.media-content--thumbnail {
  display: block;
}

.media-content--thumbnail .media-content__overlay {
  position: absolute;
  inset: 0;
}

.media-content__duration {
  position: absolute;
  top: var(--size-5);
  right: var(--size-4);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: var(--size-2);
  padding: var(--size-3) var(--size-4) var(--size-3) var(--size-3);
  border-radius: 100px;
  color: var(--ca-color-text-inverted);
  background: rgba(0, 0, 0, 0.55);
}

.media-content__duration-icon {
  width: var(--size-5);
  height: var(--size-5);
}

.media-content__overlay {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: var(--size-7);
  width: 100%;
  height: 500px;
  padding: var(--size-4);
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.55) 100%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 57.46%);
}

.media-content__cta {
  align-self: flex-start;
}

// Espaçador que garante um padding size-4 acima do module message.
// Aplica-se apenas em tablet (768–1024px).
.media-content__spacer {
  display: none;
}

@media (min-width: 768px) and (max-width: 1024px) {
  .media-content__spacer {
    display: block;
    flex: none;
    height: var(--size-4);
  }
}

// ---------------------------------------------------------------------------
// From 1025px up — align the module message to the top, keeping the CTA at the
// bottom of the overlay.
// ---------------------------------------------------------------------------
@media (min-width: 1025px) {
  .media-content__overlay {
    padding: var(--size-6);
    gap: var(--size-5);
  }

  // Thumbnail (desktop): a altura passa a ser definida pelo conteúdo do
  // overlay (alinhado ao topo), com a imagem a preencher o fundo. Deixa de
  // existir o espaço vazio por baixo do CTA.
  .media-content--thumbnail {
    min-height: 0;
  }

  .media-content--thumbnail .media-content__media {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    min-height: 0;
    object-fit: cover;
  }

  .media-content--thumbnail .media-content__overlay {
    position: relative;
    inset: auto;
    justify-content: flex-start;
  }

  .media-content__message {
    max-width: 50%;
  }

  .media-content__duration {
    top: var(--size-6);
    right: var(--size-6);
  }
}
</style>
